import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { observer } from "mobx-react-lite";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import {
  ALERT_RESOLVE_NOTE_MAX,
  ALERT_RESOLVE_NOTE_MIN,
  AlertItem,
} from "../../../../../models/Alerts/Alert";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import {
  AlertsOutcome,
  trackAlertsAction,
} from "../../../../../analytics/alertsTracking";
import {
  formatAlertDateTime,
  getAlertColors,
  getResolvedTone,
} from "./alertUi";

interface ResolveAlertDialogProps {
  alert: AlertItem | null;
  open: boolean;
  onClose: () => void;
  onResolved?: () => void;
}

const ResolveAlertDialog: React.FC<ResolveAlertDialogProps> = ({
  alert,
  open,
  onClose,
  onResolved,
}) => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const resolvedTone = getResolvedTone(theme);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");

  useEffect(() => {
    if (open) {
      setNote("");
      setNoteError("");
      alertsStore.clearResolveState();
    }
  }, [open]);

  const trimmedLength = note.trim().length;
  const conflict = alertsStore.resolveConflict;

  /** Dismiss without resolving (X, Cancel, backdrop). */
  const handleCancel = () => {
    if (alert && !conflict) {
      trackAlertsAction("resolve_cancel", "dtl", { alert });
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (!alert) return;
    if (trimmedLength < ALERT_RESOLVE_NOTE_MIN) {
      setNoteError(
        `Add a note of at least ${ALERT_RESOLVE_NOTE_MIN} characters.`,
      );
      return;
    }
    if (trimmedLength > ALERT_RESOLVE_NOTE_MAX) {
      setNoteError(`Keep the note under ${ALERT_RESOLVE_NOTE_MAX} characters.`);
      return;
    }
    setNoteError("");
    try {
      const resolved = await alertsStore.resolve(alert.id, note);
      const code = alertsStore.resolveErrorCode;
      const outcome: AlertsOutcome = resolved
        ? "success"
        : code === "ALREADY_RESOLVED"
          ? "already_resolved"
          : code === "NOT_RESOLVABLE"
            ? "not_resolvable"
            : "error";
      trackAlertsAction("resolve_submit", "dtl", { alert, outcome });
      if (resolved) {
        onResolved?.();
        onClose();
      }
    } catch (error: any) {
      setNoteError(error?.message || "Failed to resolve alert.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={alertsStore.isResolving ? undefined : handleCancel}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          boxShadow: colors.shadow,
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: resolvedTone.fg,
              bgcolor: resolvedTone.bg,
              border: `1px solid ${resolvedTone.border}`,
            }}
          >
            <TaskAltRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CustomTypography variant="heading4" color={colors.title}>
              Resolve alert
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.subtitle}
              sx={{ mt: 0.5 }}
            >
              Resolves it for everyone who received it.
            </CustomTypography>
          </Box>
          <IconButton
            size="small"
            onClick={handleCancel}
            disabled={alertsStore.isResolving}
            sx={{ color: colors.muted, borderRadius: "8px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {alert && (
          <Box
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: "10px",
              bgcolor: colors.surfaceMuted,
              border: `1px solid ${colors.border}`,
            }}
          >
            <CustomTypography variant="heading8" color={colors.subtitle}>
              {alert.ref.display}
            </CustomTypography>
            <CustomTypography
              variant="body1"
              color={colors.title}
              fontWeight={600}
              sx={{ mt: 0.5 }}
            >
              {alert.title}
            </CustomTypography>
          </Box>
        )}

        {conflict ? (
          <Box sx={{ mt: 2 }}>
            <InlineFormError message={alertsStore.resolveError} />
            {conflict.resolvedAt && (
              <CustomTypography variant="caption" color={colors.subtitle}>
                Resolved {formatAlertDateTime(conflict.resolvedAt)}. The list
                has been refreshed.
              </CustomTypography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <AppButton label="Close" variant="grayscale" onClick={onClose} />
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ mt: 2.5 }}>
              <CustomTypography
                variant="heading8"
                color={colors.text}
                sx={{ mb: 0.75 }}
              >
                Resolution note
              </CustomTypography>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                value={note}
                placeholder="What was done? e.g. Retried, paid via UPI"
                onChange={(event) => {
                  setNote(event.target.value.slice(0, ALERT_RESOLVE_NOTE_MAX));
                  if (noteError) setNoteError("");
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    (event.metaKey || event.ctrlKey)
                  ) {
                    handleSubmit();
                  }
                }}
                disabled={alertsStore.isResolving}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.85rem",
                    borderRadius: "10px",
                    bgcolor: colors.surface,
                    color: colors.title,
                    "& fieldset": { borderColor: colors.border },
                    "&:hover fieldset": { borderColor: colors.accent },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.accent,
                      borderWidth: 1,
                    },
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 0.5,
                }}
              >
                <CustomTypography variant="caption" color={colors.muted}>
                  Minimum {ALERT_RESOLVE_NOTE_MIN} characters
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={
                    trimmedLength > 0 && trimmedLength < ALERT_RESOLVE_NOTE_MIN
                      ? theme.palette.customColors.errorRed
                      : colors.muted
                  }
                >
                  {trimmedLength}/{ALERT_RESOLVE_NOTE_MAX}
                </CustomTypography>
              </Box>
              <InlineFormError
                message={noteError || alertsStore.resolveError}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <AppButton
                label="Cancel"
                variant="grayscale"
                onClick={handleCancel}
                disabled={alertsStore.isResolving}
              />
              <AppButton
                label={alertsStore.isResolving ? "Resolving…" : "Resolve alert"}
                variant="primary"
                onClick={handleSubmit}
                disabled={
                  alertsStore.isResolving ||
                  trimmedLength < ALERT_RESOLVE_NOTE_MIN
                }
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default observer(ResolveAlertDialog);
